/**
 * useRoomConnection Hook - React Hook for Room Connection Management
 *
 * Refactored to use Upstash Realtime SDK directly.
 * Maintains backwards compatibility with existing API.
 *
 * @module features/collab/transport/hooks/useRoomConnection
 * @see SPEC-007-6 Requirements 8.1-8.6
 */

import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useRealtime } from '../realtime-client';
import { useAuthStore } from '../../../auth';
import type { PresenceUpdateEvent, PresenceJoinEvent, PresenceLeaveEvent } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * SDK connection status values
 * Includes both Upstash SDK values (open/closed/connecting/reconnecting)
 * and legacy values (connected/disconnected) for backwards compatibility
 */
export type SDKConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'connecting'
  | 'reconnecting'
  | 'open'
  | 'closed';

/** Legacy connection state for API compatibility */
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

/** Options for useRoomConnection hook (legacy API) */
export interface UseRoomConnectionOptions {
  autoConnect?: boolean;
  onPresenceUpdate?: (data: PresenceUpdateEvent) => void;
  onPresenceJoin?: (data: PresenceJoinEvent) => void;
  onPresenceLeave?: (data: PresenceLeaveEvent) => void;
  onStatusChange?: (status: SDKConnectionStatus) => void;
  onError?: (error: Error) => void;
}

/** Position type for cursor/selection */
interface Position {
  line: number;
  column: number;
}

/** Selection type */
interface Selection {
  anchor: Position;
  head: Position;
}

/** Presence data type */
interface PresenceData {
  cursor: Position | null;
  selection: Selection | null;
  displayName: string;
  colorId: number;
  updatedAt: number;
}

/** Stub RoomConnection interface for backwards compatibility */
export interface RoomConnection {
  getDocId(): string;
  getUserId(): string;
  getState(): ConnectionState;
  isDegraded(): boolean;
  disconnect(): void;
  updateCursor(cursor: Position | null): void;
  updateSelection(selection: Selection | null): void;
  getParticipants(): unknown[];
  getLocalPresence(): PresenceData;
  on<K extends string>(event: K, handler: (...args: unknown[]) => void): () => void;
}

// ============================================================================
// Presence API Client
// ============================================================================

/**
 * Get API base URL from environment variable or default
 */
function getApiBaseUrl(): string {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return '';
}

/**
 * Publish presence update to server via Worker API
 */
async function publishPresenceUpdate(
  docId: string,
  userId: string,
  displayName: string,
  colorId: number,
  cursor: Position | null,
  selection: Selection | null,
  sessionToken: string | null
): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    const response = await fetch(`${getApiBaseUrl()}/realtime/presence`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        channel: `doc:${docId}`,
        event: 'presence.update',
        data: { userId, displayName, colorId, cursor, selection },
      }),
    });
    if (!response.ok) {
      console.warn(`[useRoomConnection] Presence update failed: ${response.status}`);
    }
  } catch (err) {
    console.warn('[useRoomConnection] Presence update error:', err);
  }
}

/**
 * Publish presence join to server via Worker API
 */
async function publishPresenceJoin(
  docId: string,
  userId: string,
  displayName: string,
  colorId: number,
  sessionToken: string | null
): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    const response = await fetch(`${getApiBaseUrl()}/realtime/presence`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        channel: `doc:${docId}`,
        event: 'presence.join',
        data: { userId, displayName, colorId },
      }),
    });

    if (!response.ok) {
      console.warn(`[useRoomConnection] Presence join failed: ${response.status}`);
    }
  } catch (err) {
    console.warn('[useRoomConnection] Presence join error:', err);
  }
}

/**
 * Publish presence leave to server via Worker API
 */
async function publishPresenceLeave(
  docId: string,
  userId: string,
  sessionToken: string | null
): Promise<void> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    const response = await fetch(`${getApiBaseUrl()}/realtime/presence`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        channel: `doc:${docId}`,
        event: 'presence.leave',
        data: { userId },
      }),
    });
    // Some tests mock fetch loosely; guard missing response shape to avoid noisy TypeErrors
    if (!response || typeof (response as Response).ok !== 'boolean') {
      if (!publishPresenceLeave.warnedMissingResponse) {
        console.warn('[useRoomConnection] Presence leave skipped: no response from fetch mock');
        publishPresenceLeave.warnedMissingResponse = true;
      }
      return;
    }
    if (!response.ok) {
      console.warn(`[useRoomConnection] Presence leave failed: ${response.status}`);
    }
  } catch (err) {
    console.warn('[useRoomConnection] Presence leave error:', err);
  }
}

// Track whether we've already warned about missing response to avoid log spam in tests
publishPresenceLeave.warnedMissingResponse = false as boolean;

/** Return type for useRoomConnection hook (legacy API) */
export interface UseRoomConnectionResult {
  connection: RoomConnection | null;
  state: ConnectionState;
  error: Error | null;
  isDegraded: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  retry: () => Promise<void>;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Map SDK status to legacy ConnectionState
 *
 * SDK status values (from Upstash Realtime):
 * - 'connecting' -> 'connecting'
 * - 'open' -> 'connected' (SDK uses 'open' for connected state)
 * - 'reconnecting' -> 'reconnecting'
 * - 'closed' -> 'disconnected' (SDK uses 'closed' for disconnected state)
 * - 'connected' -> 'connected' (legacy compatibility)
 * - 'disconnected' -> 'disconnected' (legacy compatibility)
 * - 'error' -> 'error'
 *
 * @see SPEC-007-6 Task 7.2 - Status value mapping
 */
function mapSDKStatusToConnectionState(status: SDKConnectionStatus): ConnectionState {
  switch (status) {
    case 'connecting':
      return 'connecting';
    case 'open':
    case 'connected':
      return 'connected';
    case 'reconnecting':
      return 'reconnecting';
    case 'closed':
    case 'disconnected':
      return 'disconnected';
    case 'error':
      return 'error';
    default:
      return 'disconnected';
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * React Hook for managing room connections using Upstash Realtime SDK
 * Maintains backwards compatibility with legacy API.
 */
export function useRoomConnection(
  docId: string,
  userId: string,
  displayName: string,
  options: UseRoomConnectionOptions = {}
): UseRoomConnectionResult {
  const {
    autoConnect = false,
    onPresenceUpdate,
    onPresenceJoin,
    onPresenceLeave,
    onStatusChange,
    onError,
  } = options;

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const sessionToken = useAuthStore((state) => state.sessionToken);
  const [error, setError] = useState<Error | null>(null);
  const [manualDisconnect, setManualDisconnect] = useState(false);
  const prevStatusRef = useRef<SDKConnectionStatus>('disconnected');
  const eventHandlersRef = useRef<Map<string, Set<(...args: unknown[]) => void>>>(new Map());
  const hasSentJoinRef = useRef(false);

  // Refs for stable callbacks and token
  const callbackRefs = useRef({
    onPresenceUpdate,
    onPresenceJoin,
    onPresenceLeave,
    onStatusChange,
    onError,
  });
  const sessionTokenRef = useRef(sessionToken);
  useEffect(() => {
    callbackRefs.current = {
      onPresenceUpdate,
      onPresenceJoin,
      onPresenceLeave,
      onStatusChange,
      onError,
    };
    sessionTokenRef.current = sessionToken;
  }, [onPresenceUpdate, onPresenceJoin, onPresenceLeave, onStatusChange, onError, sessionToken]);

  // Expected channel for this room
  const expectedChannel = `doc:${docId}`;

  // Handle incoming data from SDK - filter by channel to prevent cross-room pollution
  const handleData = useCallback(
    ({ event, data, channel }: { event: string; data: unknown; channel: string }) => {
      // Filter: only process events from our channel
      if (channel !== expectedChannel) {
        return;
      }

      switch (event) {
        case 'presence.update':
          callbackRefs.current.onPresenceUpdate?.(data as PresenceUpdateEvent);
          eventHandlersRef.current.get('presenceUpdated')?.forEach((h) => {
            const d = data as PresenceUpdateEvent;
            h(d.userId, d);
          });
          break;
        case 'presence.join':
          callbackRefs.current.onPresenceJoin?.(data as PresenceJoinEvent);
          eventHandlersRef.current.get('participantJoined')?.forEach((h) => h(data));
          break;
        case 'presence.leave':
          callbackRefs.current.onPresenceLeave?.(data as PresenceLeaveEvent);
          eventHandlersRef.current.get('participantLeft')?.forEach((h) => {
            h((data as PresenceLeaveEvent).userId);
          });
          break;
      }
    },
    [expectedChannel]
  );

  const shouldConnect = autoConnect && isAuthenticated && !!docId && !manualDisconnect;

  const { status: rawStatus } = useRealtime({
    channels: [expectedChannel],
    events: ['presence.update', 'presence.join', 'presence.leave'],
    onData: handleData,
    enabled: shouldConnect,
  });

  const status = rawStatus;
  const state = mapSDKStatusToConnectionState(status);

  // Track status changes
  useEffect(() => {
    const prev = prevStatusRef.current;
    if (status !== prev) {
      callbackRefs.current.onStatusChange?.(status);
      eventHandlersRef.current.get('stateChange')?.forEach((h) => h(state));
      if (status === 'connected') {
        setError(null);
        eventHandlersRef.current.get('connected')?.forEach((h) => h());
      }
      if (status === 'error') {
        const err = new Error('Connection error');
        setError(err);
        callbackRefs.current.onError?.(err);
        eventHandlersRef.current.get('error')?.forEach((h) => h(err));
      }
      if (status === 'disconnected' && prev === 'connected') {
        eventHandlersRef.current.get('disconnected')?.forEach((h) => h());
      }
    }
    prevStatusRef.current = status;
  }, [status, state]);

  // Local presence state for tracking cursor/selection
  const localPresenceRef = useRef<{
    cursor: Position | null;
    selection: Selection | null;
    colorId: number;
  }>({
    cursor: null,
    selection: null,
    colorId: Math.floor(Math.random() * 8), // Random color 0-7
  });
  const colorId = localPresenceRef.current.colorId;

  // Send presence.join once when connected; reset on disconnect
  useEffect(() => {
    if (state === 'connected' && shouldConnect && !hasSentJoinRef.current) {
      hasSentJoinRef.current = true;
      publishPresenceJoin(docId, userId, displayName, colorId, sessionTokenRef.current);
      publishPresenceUpdate(
        docId,
        userId,
        displayName,
        colorId,
        localPresenceRef.current.cursor,
        localPresenceRef.current.selection,
        sessionTokenRef.current
      );
    }

    if (state !== 'connected') {
      hasSentJoinRef.current = false;
    }
  }, [state, shouldConnect, docId, userId, displayName, colorId]);

  // Create RoomConnection object with real presence methods
  const connection = useMemo<RoomConnection | null>(() => {
    if (!shouldConnect) return null;

    return {
      getDocId: () => docId,
      getUserId: () => userId,
      getState: () => state,
      isDegraded: () => false,
      disconnect: () => {
        // Send presence.leave when disconnecting
        publishPresenceLeave(docId, userId, sessionTokenRef.current);
      },
      updateCursor: (cursor: Position | null) => {
        localPresenceRef.current.cursor = cursor;
        // Publish presence update to server
        publishPresenceUpdate(
          docId,
          userId,
          displayName,
          colorId,
          cursor,
          localPresenceRef.current.selection,
          sessionTokenRef.current
        );
      },
      updateSelection: (selection: Selection | null) => {
        localPresenceRef.current.selection = selection;
        // Publish presence update to server
        publishPresenceUpdate(
          docId,
          userId,
          displayName,
          colorId,
          localPresenceRef.current.cursor,
          selection,
          sessionTokenRef.current
        );
      },
      getParticipants: () => [],
      getLocalPresence: () => ({
        cursor: localPresenceRef.current.cursor,
        selection: localPresenceRef.current.selection,
        displayName,
        colorId,
        updatedAt: Date.now(),
      }),
      on: (event, handler) => {
        if (!eventHandlersRef.current.has(event)) {
          eventHandlersRef.current.set(event, new Set());
        }
        eventHandlersRef.current.get(event)!.add(handler);
        return () => {
          eventHandlersRef.current.get(event)?.delete(handler);
        };
      },
    };
  }, [shouldConnect, docId, userId, displayName, state]);

  // Send presence.leave on unmount
  useEffect(() => {
    return () => {
      if (shouldConnect) {
        publishPresenceLeave(docId, userId, sessionTokenRef.current);
      }
    };
  }, [shouldConnect, docId, userId]);

  // Manual connection control methods
  const connect = useCallback(async () => {
    setManualDisconnect(false);
    setError(null);
  }, []);

  const disconnect = useCallback(() => {
    publishPresenceLeave(docId, userId, sessionTokenRef.current);
    setManualDisconnect(true);
  }, [docId, userId]);

  const retry = useCallback(async () => {
    setError(null);
    setManualDisconnect(false);
  }, []);

  return { connection, state, error, isDegraded: false, connect, disconnect, retry };
}
